import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  company: {
    id: string;
    name: string;
    industry: string;
    employees: number;
    establishedYear: number;
  };
  role: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Mock user data for demo
const mockUser: User = {
  id: '1',
  email: 'admin@riyadh-trading.com',
  name: 'أحمد محمد الرياض',
  company: {
    id: 'comp-1',
    name: 'شركة الرياض للتجارة',
    industry: 'تجارة التجزئة',
    employees: 145,
    establishedYear: 2018
  },
  role: 'admin'
};

// Demo credentials
const DEMO_CREDENTIALS = {
  email: 'demo@flyn.sa',
  password: 'demo123'
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    const checkAuth = () => {
      try {
        const savedUser = localStorage.getItem('flyn_user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        localStorage.removeItem('flyn_user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check demo credentials
      if (email === DEMO_CREDENTIALS.email && password === DEMO_CREDENTIALS.password) {
        setUser(mockUser);
        localStorage.setItem('flyn_user', JSON.stringify(mockUser));
        setIsLoading(false);
        return true;
      }
      
      // For demo purposes, accept any email with "demo" in it
      if (email.includes('demo') || email.includes('test')) {
        const demoUser = {
          ...mockUser,
          email: email,
          name: email.split('@')[0].replace(/[._]/g, ' ')
        };
        setUser(demoUser);
        localStorage.setItem('flyn_user', JSON.stringify(demoUser));
        setIsLoading(false);
        return true;
      }
      
      setIsLoading(false);
      return false;
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('flyn_user');
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
